import React, { useEffect, useState, useContext } from "react"
import AvailabilityRulesSelect from "../AvailabilityRulesSelect"
import currencyCodesData from "./currencyCodesData.json"
import FlexPaymentDetails from "../FlexPaymentsDetails"
import InstallmentDetails from "./InstallmentDetails"
import SyncPrompt from "../SyncPrompt"
import useLoading from "../../../hooks/useLoading"
import { Button } from "../../ui/button"
import { ClientPaymentRequests } from "../../../api/app.service"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu"
import { Label } from "../../ui/label"
import { Switch } from "../../ui/switch"
import { SvgWarning, SvgFilledInformationCircle } from "../../icons"
import { UserCtx } from "../../../context/userContext"
import { useToast } from "../../../hooks/useToast"

interface Props {
  formErrors: any
  serviceValues: any
  setServiceValues: any
  handleSessionCostChange: any
  serviceType: any
  availabilities: any
  handleAvailabilityRulesChange: any
  isGroupService: boolean
  service: any
}

interface CurrencyCodes {
  code: string
  symbol: string
}

const SingleServiceDetailsForm: React.FC<Props> = ({
  formErrors,
  serviceValues,
  setServiceValues,
  handleSessionCostChange,
  serviceType,
  availabilities,
  handleAvailabilityRulesChange,
  isGroupService,
  service,
}) => {
  const { user } = useContext(UserCtx)
  const { toast } = useToast()
  const { startLoading, stopLoading } = useLoading()

  const [showSyncPrompt, setShowSyncPrompt] = useState(false)
  const [paypalStatus, setPaypalStatus] = useState<any>(null)
  const [showInformation, setShowInformation] = useState<boolean>(false)
  const [currencyCodes, setCurrencyCodes] = useState<CurrencyCodes[]>([])

  const isLocked =
    (service?.enrolled_count ?? 0) >= 1 &&
    String(service?.is_group_service) === "true"

  useEffect(() => {
    if (serviceValues.currency === "") {
      const defaultCurrency = {
        code: "USD",
        symbol: "\\u0024",
      }
      setServiceValues((prevValues: typeof serviceValues) => ({
        ...prevValues,
        currency: defaultCurrency.code,
        symbol: defaultCurrency.symbol,
      }))
      handleCurrencySelect(defaultCurrency)
    }
  }, [])

  const showSessionCost = () => {
    let showCost = false
    if (!serviceValues?.group?.isFree || !serviceValues.isFree) {
      showCost = true
    }
    return showCost
  }

  useEffect(() => {
    if (
      serviceValues.isFree ||
      (serviceValues.group.isFree && serviceType === "group")
    ) {
      setServiceValues({
        ...serviceValues,
        sessionCost: 0.0,
        allowPromo: false,
      })
    }
  }, [serviceValues.isFree, serviceValues.group.isFree, serviceType])

  useEffect(() => {
    const sessionCostInput = document.getElementById("sessionCostInput") || null
    if (sessionCostInput) {
      sessionCostInput.addEventListener("keydown", function (e) {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault()
        }
      })
    }
  }, [])

  useEffect(() => {
    const data: CurrencyCodes[] = currencyCodesData.codes
    setCurrencyCodes(data)
  }, [])

  const decodeUnicodeSymbol = (unicodeStr: string | undefined | null) => {
    if (!unicodeStr) {
      return ""
    }
    return unicodeStr.replace(/\\u([\dA-F]{4})/gi, (match, grp) => {
      return String.fromCharCode(parseInt(grp, 16))
    })
  }

  const handleCurrencySelect = (code: CurrencyCodes) => {
    setServiceValues({
      ...serviceValues,
      currency: code.code,
      symbol: code.symbol,
    })
  }

  const getPaypalOnboardingStatus = async () => {
    if (user) {
      startLoading()
      try {
        const onboardingStatus =
          await ClientPaymentRequests.getPaypalOnboardingStatus({
            seller_payer_id: "",
            seller_user_id: parseInt(user?.user_id),
          })

        setPaypalStatus(onboardingStatus)
      } catch (error) {
        toast.error(
          "An error occurred trying to fetch your PayPal onboarding status"
        )
      } finally {
        stopLoading()
      }
    }
  }

  useEffect(() => {
    getPaypalOnboardingStatus()
  }, [])

  const checkStatus = () => {
    if (!paypalStatus?.data.onboarding_completed) {
      setShowSyncPrompt(true)
      return false
    }
    return true
  }

  return (
    <>
      <h4 className="font-bold text-base mb-[16px]">
        What is the length of each session?
      </h4>
      <div
        className={`${
          formErrors?.sessionLength && "input-error"
        } flex items-center gap-[12px] mb-[20px]`}
      >
        <input
          type="number"
          min="0"
          max="60"
          className="w-[54px] h-[54px] bg-white focus:outline-0 border
                    border-1 border-grayCloud rounded-[10px] text-graySlate text-center"
          value={serviceValues.sessionLength.minutes}
          onWheel={() => (document.activeElement as HTMLElement)?.blur()}
          name="sessionMinutes"
          onChange={(e: any) =>
            setServiceValues({
              ...serviceValues,
              sessionLength: { minutes: e.target.value },
            })
          }
        />
        <span className="font-bold text-base">minutes</span>
      </div>
      {formErrors.sessionLength && (
        <div className="field-error w-[100%]">
          <SvgWarning />
          {formErrors.sessionLength}
        </div>
      )}

      {/* SELECT WITH EXISTING AVAILABILITY RULES */}
      {availabilities?.length > 0 && (
        <AvailabilityRulesSelect
          availabilities={availabilities}
          serviceType={serviceType}
          serviceValues={serviceValues}
          handleAvailabilityRulesChange={handleAvailabilityRulesChange}
          service={service}
        />
      )}

      <div className="w-full h-[1px] bg-grayMist my-[32px]"></div>

      {/* GROUP IS FREE TOGGLE */}
      {isGroupService && (
        <>
          <div className="flex flex-col mb-[20px]">
            <h4
              className={`font-bold text-base mb-[8px] ${
                service.enrolled_count >= 1 ? "text-graySlate" : ""
              }`}
            >
              Is this a free group?
            </h4>
            <div className="flex items-center gap-2">
              <Switch
                onCheckedChange={() => {
                  if (!checkStatus()) return
                  setServiceValues({
                    ...serviceValues,
                    isFree: !serviceValues.isFree,
                    // sessionCost: serviceValues,
                    group: {
                      ...serviceValues.group,
                      isFree: !serviceValues.group.isFree,
                    },
                  })
                }}
                checked={
                  serviceValues.group.isFree &&
                  serviceValues.isFree &&
                  serviceValues.sessionCost === 0
                }
                id="free"
              />
              <Label htmlFor="free">Free</Label>
            </div>
          </div>
        </>
      )}
      {!isGroupService && (
        <div className="flex flex-col mb-[20px]">
          <h4
            className={`font-bold text-base mb-[8px] ${
              service.enrolled_count >= 1 ? "text-graySlate" : ""
            }`}
          >
            Is this a free service?
          </h4>
          <div className="flex items-center gap-2">
            <Switch
              onCheckedChange={() => {
                if (!checkStatus()) return

                setServiceValues({
                  ...serviceValues,
                  isFree: !serviceValues.isFree,
                })
              }}
              checked={serviceValues.isFree}
              id="free"
            />
            <Label htmlFor="free">Free</Label>
          </div>
        </div>
      )}

      {/* SERVICE COST IF NOT FREE */}
      {showSessionCost() && (
        <div className={`flex flex-col mb-[20px]`}>
          <h4
            className={`font-bold text-base mb-[8px] ${
              service.enrolled_count >= 1 ? "text-graySlate" : ""
            }`}
          >
            Session cost
          </h4>
          <fieldset
            disabled={isLocked}
            className={`relative w-full ${
              isLocked ? "opacity-50 pointer-events-none" : ""
            }`}
          >
            <div
              className={`relative ${
                formErrors?.sessionCost && "input-error"
              } default-input w-full`}
            >
              <div className="flex">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="icon" variant="utility" className="h-[40px]">
                      {serviceValues.currency}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel className="text-graySlate text-xs p-2">
                      Currency
                    </DropdownMenuLabel>
                    <DropdownMenuGroup>
                      {currencyCodes.map((code, index) => (
                        <DropdownMenuItem
                          onClick={() => handleCurrencySelect(code)}
                        >
                          {code.code}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Input Box */}
                <input
                  id="sessionCostInput"
                  type="number"
                  step="1"
                  min="0"
                  autoComplete="off"
                  name="sessionCost"
                  placeholder="Cost"
                  value={serviceValues.sessionCost}
                  onWheel={() =>
                    (document.activeElement as HTMLElement)?.blur()
                  }
                  onChange={handleSessionCostChange}
                  className="ml-2 flex-1 border border-gray-300 py-2 px-4 h-[40px] focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Error Message */}
              {formErrors.sessionCost && (
                <div className="field-error w-full">
                  <SvgWarning />
                  {formErrors.sessionCost}
                </div>
              )}
            </div>
          </fieldset>
        </div>
      )}

      {showSessionCost() && (
        <>
          <div className="flex flex-col relative">
            <div className="flex items-center gap-1 mb-[8px]">
              <h4 className="font-bold text-base">Enable flexible payments?</h4>

              <SvgFilledInformationCircle
                className="h-4 w-4 cursor-pointer"
                onMouseEnter={() => setShowInformation(true)}
                onMouseLeave={() => setShowInformation(false)}
              />
            </div>

            {showInformation && (
              <div className="absolute left-0 top-[28px] z-20 p-3 text-sm font-medium rounded-md border border-gray-300 bg-white w-[254px] shadow-lg">
                <h3 className="font-semibold mb-1">Flexible Payments</h3>
                <p>
                  Give clients the choice to pay in full or choose from multiple
                  payment plan options — all within a single package. Perfect
                  for high-ticket programs where clients may need different ways
                  to pay.
                </p>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Switch
                id="flexible"
                checked={serviceValues.payment_term === "flexible"}
                onCheckedChange={(checked) =>
                  setServiceValues({
                    ...serviceValues,
                    payment_term: checked ? "flexible" : "full_pay",
                  })
                }
              />
              <Label htmlFor="flexible">Enabled</Label>
            </div>
          </div>

          <div className="flex flex-col mt-4">
            <h4 className="font-bold text-base mb-[8px]">
              Enable clients to use your promo codes?
            </h4>

            <div className="flex items-center gap-2">
              <Switch
                onCheckedChange={() =>
                  setServiceValues({
                    ...serviceValues,
                    allowPromo: !serviceValues.allowPromo,
                  })
                }
                checked={serviceValues.allowPromo}
                id="promo"
              />
              <Label htmlFor="promo">Enabled</Label>
            </div>
          </div>
        </>
      )}

      {showSessionCost() &&
        (serviceValues.payment_term === "flexible" ||
          serviceValues.payment_term === "installment") && (
          <InstallmentDetails
            formErrors={formErrors}
            isLocked={isLocked}
            serviceValues={serviceValues}
            setServiceValues={setServiceValues}
          />
        )}

      {showSessionCost() &&
        (serviceValues.payment_term !== "flexible" ? (
          <>
            {/* BILLING PREVIEW */}
            <div className="w-full h-px bg-grayMist my-[32px]"></div>
            <h4 className="font-bold text-base mb-[8px]">Preview</h4>

            <div className="w-full border rounded-xl p-[20px] flex flex-col">
              <h5 className="text-graySlate text-base mb-[32px]">
                Payment Details
              </h5>

              <div className="flex flex-col sm:flex-row w-full gap-2 sm:items-center mb-[16px] min-w-0">
                {" "}
                <div className="flex items-center gap-[16px] min-w-0">
                  <div className="w-[24px] h-[24px] bg-blurple rounded-full text-white font-bold justify-center items-center flex shrink-0">
                    {decodeUnicodeSymbol(serviceValues.symbol)}
                  </div>
                  <p className="text-[16px] break-words sm:whitespace-nowrap">
                    Payment at checkout
                  </p>
                </div>
                <div className="mx-[24px] w-full border-b border-grayCloud transform -translate-y-1 border-dashed h-2"></div>
                <span className="text-base">
                  {decodeUnicodeSymbol(serviceValues.symbol)}
                  {serviceType === "consultation" &&
                  serviceValues?.consultation?.isFree
                    ? 0.0
                    : isNaN(serviceValues?.sessionCost)
                    ? "0.00"
                    : serviceValues?.sessionCost.toFixed(2)}
                </span>
              </div>

              <div className="text-graySlate w-full bg-grayMist rounded-lg p-[26px] flex items-center justify-center text-base">
                <p className="text-center">
                  Payment will be charged{" "}
                  <strong className="text-black">immediately</strong> at
                  checkout.
                </p>
              </div>
            </div>
          </>
        ) : (
          <>
            <FlexPaymentDetails
              serviceValues={serviceValues}
              serviceType={serviceType}
            />
          </>
        ))}
      {showSyncPrompt && (
        <SyncPrompt setSyncPrompt={setShowSyncPrompt} service={service} />
      )}
    </>
  )
}

export default SingleServiceDetailsForm
